import { useAuth } from "@elit-hair/elit-tools";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageFiltersDialog } from "@/components/page-filters-dialog";
import { ErrorState } from "@/components/ui/error-state";
import { SIMULATED_CALL_PROVIDER } from "@/constants/call.dev";
import { CUSTOM_LEAD_LIST_KEY, getLeadListLabel } from "@/constants/lead-lists.constants";
import { useCall } from "@/providers/hooks/use-call";
import { usePageFilters } from "@/providers/hooks/use-page-filters";
import { LeadWithAssignment, leadKeys, useUnassignLeads } from "@/queries/lead.queries";
import { APP_ROUTES_FULL } from "@/utils/routes";

import { LeadsList } from "./components/leads-list";
import { ListsFilters } from "./components/lists-filters";
import { useListsFilters } from "./hooks/use-lists-filters";
import { getFilterSummaryLabels } from "./utils/lead-list-filter.utils";

export const LIST_STYLES = {
    default: {
        header: "flex-shrink-0 mb-3 max-w-2xl mx-auto w-full lg:mb-4",
        content: "flex min-h-0 flex-1 flex-col overflow-hidden lg:overflow-y-auto",
        items: "flex min-h-0 flex-1 flex-col lg:grid lg:gap-4 lg:pb-4 lg:max-w-2xl lg:mx-auto lg:w-full",
    },
    compact: {
        header: "flex-shrink-0 mb-2 max-w-full mx-auto w-full",
        content: "flex min-h-0 flex-1 flex-col overflow-hidden lg:overflow-y-auto",
        items: "flex min-h-0 flex-1 flex-col lg:grid lg:gap-2 lg:pb-2 lg:max-w-full lg:mx-auto lg:w-full",
    },
    expanded: {
        header: "flex-shrink-0 mb-3 max-w-4xl mx-auto w-full lg:mb-6",
        content: "flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-none lg:overflow-visible",
        items: "flex h-0 min-h-0 flex-1 flex-col overflow-hidden lg:h-auto lg:flex-none lg:overflow-visible",
    },
} as const;

export const Route = createFileRoute(APP_ROUTES_FULL.LISTS)({
    component: Lists,
});

function Lists() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { onCallEnded } = useCall();
    const { userData } = useAuth();
    const unassignMutation = useUnassignLeads();
    const { setFiltersOpen } = usePageFilters();
    const {
        selectedListKey,
        selectedFilter,
        isCustomList,
        customLanguages,
        customLayouts,
        customStatuses,
        customSettingPriorityMin,
        customSettingPriorityMax,
        setList,
        setCustomLanguages,
        setCustomLayouts,
        setCustomStatuses,
        setCustomSettingPriorityMin,
        setCustomSettingPriorityMax,
    } = useListsFilters();

    const [error, setError] = useState<string | null>(null);
    const [isReassigning, setIsReassigning] = useState(false);

    const handleCallCompleted = useCallback(
        (recordId: string) => {
            if (!selectedFilter) return;
            const queryKey = leadKeys.leads(selectedFilter);
            const leads = queryClient.getQueryData<LeadWithAssignment[]>(queryKey);
            if (!leads?.some((lead) => lead.zohoLeadId === recordId)) return;
            queryClient.setQueryData<LeadWithAssignment[]>(queryKey, (old) =>
                old?.filter((lead) => lead.zohoLeadId !== recordId)
            );
            queryClient.invalidateQueries({ queryKey });
        },
        [queryClient, selectedFilter]
    );

    const applyFilterChangeWithUnassign = useCallback(
        async (applyChange: () => void) => {
            setError(null);
            setIsReassigning(true);

            try {
                await unassignMutation.mutateAsync();
                applyChange();
                await queryClient.invalidateQueries({ queryKey: leadKeys.all });
            } catch {
                setError(t("error_loading_leads"));
            } finally {
                setIsReassigning(false);
            }
        },
        [unassignMutation, t, queryClient]
    );

    const handleListChange = useCallback(
        (newListKey: string) => {
            if (newListKey === selectedListKey) {
                if (newListKey !== CUSTOM_LEAD_LIST_KEY) {
                    setFiltersOpen(false);
                }
                return;
            }

            void applyFilterChangeWithUnassign(() => setList(newListKey));

            if (newListKey !== CUSTOM_LEAD_LIST_KEY) {
                setFiltersOpen(false);
            }
        },
        [selectedListKey, applyFilterChangeWithUnassign, setList, setFiltersOpen]
    );

    const handleCustomFilterChange = useCallback(
        (applyValue: () => void, isSameValue: boolean) => {
            if (isSameValue) return;
            void applyFilterChangeWithUnassign(applyValue);
        },
        [applyFilterChangeWithUnassign]
    );

    useEffect(() => {
        return onCallEnded((call) => {
            if (call.provider === SIMULATED_CALL_PROVIDER) return;
            call.recordId && handleCallCompleted(call.recordId);
        });
    }, [onCallEnded, handleCallCompleted]);

    const currentStyle = LIST_STYLES.expanded;

    const filterSummary = useMemo(() => {
        if (!selectedFilter) {
            return [{ id: "list", label: getLeadListLabel(selectedListKey) }];
        }

        const summaryLabels = getFilterSummaryLabels(selectedFilter);

        return [
            { id: "list", label: getLeadListLabel(selectedListKey) },
            ...summaryLabels.map((label, index) => ({ id: `filter-${index}`, label })),
        ];
    }, [selectedFilter, selectedListKey]);

    return (
        <div className="flex h-0 min-h-0 flex-1 flex-col overflow-hidden w-full lg:h-auto lg:flex-none lg:overflow-visible">
            <PageFiltersDialog summary={filterSummary} contentClassName="sm:max-w-xl">
                <ListsFilters
                    selectedListKey={selectedListKey}
                    onListChange={handleListChange}
                    customLanguages={customLanguages}
                    onCustomLanguagesChange={(values) =>
                        handleCustomFilterChange(
                            () => setCustomLanguages(values),
                            customLanguages.join(",") === values.join(",")
                        )
                    }
                    customLayouts={customLayouts}
                    onCustomLayoutsChange={(values) =>
                        handleCustomFilterChange(
                            () => setCustomLayouts(values),
                            customLayouts.join(",") === values.join(",")
                        )
                    }
                    customStatuses={customStatuses}
                    onCustomStatusesChange={(values) =>
                        handleCustomFilterChange(
                            () => setCustomStatuses(values),
                            customStatuses.join(",") === values.join(",")
                        )
                    }
                    customSettingPriorityMin={customSettingPriorityMin}
                    onCustomSettingPriorityMinChange={(value) =>
                        handleCustomFilterChange(
                            () => setCustomSettingPriorityMin(value),
                            value === customSettingPriorityMin
                        )
                    }
                    customSettingPriorityMax={customSettingPriorityMax}
                    onCustomSettingPriorityMaxChange={(value) =>
                        handleCustomFilterChange(
                            () => setCustomSettingPriorityMax(value),
                            value === customSettingPriorityMax
                        )
                    }
                />
            </PageFiltersDialog>
            <div className={currentStyle.content}>
                {error ? (
                    <ErrorState
                        message={error}
                        onRetry={() => {
                            setError(null);
                            selectedFilter &&
                                queryClient.invalidateQueries({ queryKey: leadKeys.leads(selectedFilter) });
                        }}
                        retryLabel={t("retry")}
                        className="min-h-[400px]"
                    />
                ) : isCustomList && !selectedFilter ? (
                    <ErrorState
                        message={t("lists_custom_language_required", { defaultValue: "Select at least one language." })}
                        className="min-h-[400px]"
                    />
                ) : (
                    <LeadsList
                        listStyles={currentStyle}
                        selectedFilter={selectedFilter}
                        isReassigning={isReassigning}
                        utmCampaignEmail={userData?.id}
                    />
                )}
            </div>
        </div>
    );
}
